import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState, type ReactElement } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { isApiError, newClientId, startConversation } from '../../api';
import { enabledLines, invalidateConversationLists, useLines } from '../conversation';
import { lineNameWithPhone } from '../conversations/format';
import { Field, SelectField } from '../admin/Field';
import forms from '../admin/forms.module.css';
import { DialogFooter } from './DialogFooter';
import { Modal } from './Modal';
import { TemplateChooser } from './TemplateChooser';
import { PHONE_ERROR_CODES, phoneProblem, templateErrorMessage } from './form';
import { useTemplatePicker } from './use-template-picker';
import { useTemplates } from './use-templates';

/**
 * "Nova conversa": o número do escritório, o telefone do cliente e um modelo de mensagem aprovado. O
 * servidor cria o contato e a conversa (da pessoa que a abre) e enfileira o modelo; se já havia uma
 * conversa com esse número e a pessoa pode escrever nela, o modelo vai nela. Enviado, a janela fecha e a
 * conversa abre.
 */
export function NewConversationDialog({ onClose }: { onClose: () => void }): ReactElement {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { search } = useLocation();
  const lines = enabledLines(useLines().data);
  const [lineChoice, setLineChoice] = useState('');
  // Com um só número ligado, ele já é o escolhido; com vários, a pessoa escolhe (o primeiro vem marcado).
  const lineId = lines.find((l) => l.id === lineChoice)?.id ?? lines[0]?.id ?? '';
  // Os modelos são da CONTA do número escolhido (uma conexão pode ter várias contas).
  const templates = useTemplates(lineId || undefined);

  // Um reenvio idêntico reaproveita o identificador (o servidor não duplica); qualquer mudança o renova.
  const clientId = useRef(newClientId());
  const renewClientId = useCallback(() => {
    clientId.current = newClientId();
  }, []);
  const picker = useTemplatePicker(templates.data?.templates ?? [], renewClientId);

  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const phoneField = useRef<HTMLInputElement>(null);

  const oneLine = lines.length === 1 ? lines[0] : undefined;

  const mutation = useMutation({
    mutationFn: startConversation,
    onSuccess: () => {
      void invalidateConversationLists(queryClient);
    },
  });
  const pending = mutation.isPending;

  const submit = (): void => {
    if (pending) return;
    setFormError(null);
    const phoneIssue = phoneProblem(phone);
    setPhoneError(phoneIssue);
    const template = picker.validate();
    if (phoneIssue) {
      phoneField.current?.focus();
      return;
    }
    if (!template) return;
    if (!lineId) {
      setFormError('Não há nenhum número do WhatsApp ligado para enviar.');
      return;
    }
    mutation.mutate(
      {
        lineId,
        phone: phone.trim(),
        ...(name.trim() ? { name: name.trim() } : {}),
        template,
        clientId: clientId.current,
      },
      {
        onSuccess: ({ conversationId }) => {
          onClose();
          void navigate({ pathname: `/c/${encodeURIComponent(conversationId)}`, search });
        },
        onError: (error) => {
          const message = templateErrorMessage(error);
          if (isApiError(error) && PHONE_ERROR_CODES.has(error.code)) {
            setPhoneError(message);
            phoneField.current?.focus();
            return;
          }
          if (isApiError(error) && error.code === 'template_not_found') void templates.refetch();
          setFormError(message);
        },
      },
    );
  };

  return (
    <Modal
      title="Nova conversa"
      description="O cliente recebe um modelo de mensagem aprovado e pode responder normalmente."
      onClose={onClose}
      busy={pending}
    >
      <form
        className={forms.form}
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        {oneLine ? (
          <p className={forms.hint}>Enviado pelo número {lineNameWithPhone(oneLine)}.</p>
        ) : (
          lines.length > 1 && (
            <SelectField
              label="Enviar pelo número"
              value={lineId}
              disabled={pending}
              onChange={(event) => {
                setLineChoice(event.target.value);
                renewClientId();
              }}
            >
              {lines.map((line) => (
                <option key={line.id} value={line.id}>
                  {lineNameWithPhone(line)}
                </option>
              ))}
            </SelectField>
          )
        )}
        <Field
          ref={phoneField}
          data-autofocus=""
          label="Telefone do cliente"
          hint="Com DDD, por exemplo (11) 98888-7777. Fora do Brasil, comece por + e o código do país."
          type="tel"
          inputMode="tel"
          autoComplete="off"
          value={phone}
          error={phoneError}
          readOnly={pending}
          onChange={(event) => {
            setPhone(event.target.value);
            setPhoneError(null);
            renewClientId();
          }}
        />
        <Field
          label="Nome do cliente (opcional)"
          autoComplete="off"
          maxLength={120}
          value={name}
          readOnly={pending}
          onChange={(event) => {
            setName(event.target.value);
            renewClientId();
          }}
        />
        <TemplateChooser templates={templates} picker={picker} busy={pending} />
        <DialogFooter
          error={formError}
          pending={pending}
          submitLabel="Enviar modelo"
          onCancel={onClose}
        />
      </form>
    </Modal>
  );
}
